"use client";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getShops, reset } from "@/state/shops/shop.slice";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Plus, Pencil, Trash, Store } from "lucide-react";
import ShopForm from "./components/ShopForm";
import DeleteShopDialog from "./components/DeleteShopDialog";

export default function ShopsPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { shops, isLoading, error, message, success } = useSelector((state) => ({
    shops: state.shop?.shops || [],
    isLoading: state.shop?.isLoading,
    error: state.shop?.error,
    message: state.shop?.message,
    success: state.shop?.success,
  }));

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "",
      zip: "",
    },
    contact: {
      email: "",
      phone: "",
      website: "",
    },
    currency: "GHS",
    tax_rate: 0,
    payment_methods: ["cash"],
  });

  useEffect(() => {
    dispatch(getShops());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          typeof error === "string" ? error : error?.message || "An error occurred",
      });
      dispatch(reset());
    }
    if (success && message) {
      toast({
        title: "Success",
        description: message,
      });
      setIsAddDialogOpen(false);
      resetForm();
      dispatch(getShops());
      dispatch(reset());
    }
  }, [error, success, message, toast, dispatch]);

  const handleEdit = (shop) => {
    setSelectedShop(shop);
    setFormData({
      name: shop.name || "",
      description: shop.description || "",
      address: {
        street: shop.address?.street || "",
        city: shop.address?.city || "",
        state: shop.address?.state || "",
        country: shop.address?.country || "",
        zip: shop.address?.zip || "",
      },
      contact: {
        email: shop.contact?.email || "",
        phone: shop.contact?.phone || "",
        website: shop.contact?.website || "",
      },
      currency: shop.currency || "USD",
      tax_rate: shop.tax_rate || 0,
      payment_methods: shop.payment_methods || ["cash"],
    });
    setIsAddDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      address: { street: "", city: "", state: "", country: "", zip: "" },
      contact: { email: "", phone: "", website: "" },
      currency: "USD",
      tax_rate: 0,
      payment_methods: ["cash"],
    });
    setSelectedShop(null);
  };

  const handleAddClick = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Store className="h-6 w-6" />
          <h1 className="text-3xl font-bold">Shops</h1>
        </div>
        <Button onClick={handleAddClick}>
          <Plus className="mr-2 h-4 w-4" />
          Add Shop
        </Button>
      </div>

      {isLoading && !shops.length ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Tax Rate</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shops.map((shop) => (
                <TableRow key={shop._id}>
                  <TableCell className="font-medium">{shop.name}</TableCell>
                  <TableCell>{shop.contact?.email || "-"}</TableCell>
                  <TableCell>{shop.contact?.phone || "-"}</TableCell>
                  <TableCell>
                    {shop.address?.city ? `${shop.address.city}, ${shop.address.state}` : "-"}
                  </TableCell>
                  <TableCell>{shop.currency}</TableCell>
                  <TableCell>{shop.tax_rate}%</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(shop)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        setSelectedShop(shop);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!shops.length && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400 dark:text-gray-500">
                    No shops found. Click "Add Shop" to create one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ShopForm
        isOpen={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          resetForm();
        }}
        selectedShop={selectedShop}
        formData={formData}
        setFormData={setFormData}
        isLoading={isLoading}
      />

      <DeleteShopDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedShop(null);
        }}
        shop={selectedShop}
      />
    </div>
  );
}
